import React, { useState } from 'react';
import { Box, Button, LinearProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { GetInterventionSuppliersResponse, InterventionLadderStepModel, InterventionSupplierModel, InterventionSupplierType } from '../../../api-service/clients';
import { CircularProgressCentered } from '../../../components/circular-progress-centered';
import { formatNumber } from '../../../utilities';
import { externalCostColor, internalCostColor, unknownSupplierColor } from '../intervention-ladder/intervention-ladder-colors';
import { formatActivitiesWithUnknownSupplier, formatSuppliers } from '../intervention-ladder/intervention-suppliers';
import { InterventionSupplierChart } from './intervention-supplier-chart';

// The first load keeps the chart's place, so the page does not jump when it arrives.
const chartWithLegendHeight = 328;

const supplierTypes: Record<InterventionSupplierType, { label: string; color: string }> = {
  [InterventionSupplierType.Internal]: { label: 'Intern', color: internalCostColor },
  [InterventionSupplierType.External]: { label: 'Ekstern', color: externalCostColor },
  [InterventionSupplierType.Unknown]: { label: 'Ukendt', color: unknownSupplierColor },
};

interface SupplierFilter {
  paragraph: string;
  type?: InterventionSupplierType;
}

export interface InterventionSuppliersComponentProps {
  step: InterventionLadderStepModel;
  periodLabel: string;
  suppliers: GetInterventionSuppliersResponse | undefined;
  isLoading: boolean;
  hasFailed: boolean;
}

export function InterventionSuppliersComponent({ step, periodLabel, suppliers, isLoading, hasFailed }: InterventionSuppliersComponentProps) {
  return (
    <Paper sx={{ p: 2, mt: 2, position: 'relative', overflow: 'hidden' }} aria-busy={isLoading}>
      {isLoading && suppliers !== undefined && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3 }} />}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
        <Box>
          <Typography variant="h2" sx={{ fontSize: '1rem', mb: 0.5 }}>
            Leverandører
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', fontStyle: 'italic', color: 'text.secondary' }}>
            Antal interne og eksterne leverandører af indsatser pr. paragraf på{' '}
            <Box component="strong" sx={{ fontStyle: 'normal', fontWeight: 600, color: 'text.primary' }}>
              Trin {step.step} · {step.title}
            </Box>{' '}
            · {periodLabel.toLowerCase()}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, whiteSpace: 'nowrap', fontSize: '0.8125rem', color: 'text.secondary' }}>
          Valgt trin
          <Box component="strong" sx={{ fontSize: '0.9375rem', fontWeight: 600, color: '#16323d' }}>
            Trin {step.step}
          </Box>
        </Box>
      </Box>
      <InterventionSuppliersContent step={step} suppliers={suppliers} isLoading={isLoading} hasFailed={hasFailed} />
    </Paper>
  );
}

interface InterventionSuppliersContentProps {
  step: InterventionLadderStepModel;
  suppliers: GetInterventionSuppliersResponse | undefined;
  isLoading: boolean;
  hasFailed: boolean;
}

function InterventionSuppliersContent({ step, suppliers, isLoading, hasFailed }: InterventionSuppliersContentProps) {
  const [filter, setFilter] = useState<SupplierFilter>();

  if (hasFailed) {
    return <Typography>Leverandørerne kunne ikke hentes.</Typography>;
  }
  if (suppliers === undefined) {
    return (
      <Box sx={{ height: chartWithLegendHeight, display: 'grid', placeItems: 'center' }}>
        <CircularProgressCentered />
      </Box>
    );
  }
  if (suppliers.paragraphs.length === 0) {
    return <Typography>Trinnet har ingen paragraffer.</Typography>;
  }

  const { paragraphs, total } = suppliers;
  const rows = suppliers.suppliers.filter(row => filter === undefined || (row.paragraph === filter.paragraph && (filter.type === undefined || row.type === filter.type)));

  function onSelectInChart(paragraphIndex: number, type?: InterventionSupplierType) {
    setFilter({ paragraph: paragraphs[paragraphIndex].paragraph, type });
  }

  return (
    <Box sx={{ opacity: isLoading ? 0.5 : 1, transition: 'opacity 150ms' }}>
      <SupplierLegend />
      {total.total > 0 ? (
        <InterventionSupplierChart
          labels={paragraphs.map(paragraph => paragraph.paragraph)}
          internal={paragraphs.map(paragraph => paragraph.suppliers.internal)}
          external={paragraphs.map(paragraph => paragraph.suppliers.external)}
          onSelect={onSelectInChart}
        />
      ) : (
        <Box sx={{ height: 300, display: 'grid', placeItems: 'center', color: 'text.secondary', fontSize: '0.875rem' }}>Ingen leverandører</Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          gap: 2,
          mt: 2,
          py: 1.5,
          borderTop: 1,
          borderBottom: 1,
          borderColor: 'divider',
          fontSize: '0.84rem',
          color: 'text.secondary',
          '& strong': { color: 'text.primary', fontWeight: 600 },
        }}
      >
        <span>
          Interne <strong>{formatNumber(total.internal)}</strong>
        </span>
        <span>
          Eksterne <strong>{formatNumber(total.external)}</strong>
        </span>
        <span>
          I alt{' '}
          <strong>
            {formatNumber(total.total)} {formatSuppliers(total.total)}
          </strong>
        </span>
        {total.activitiesWithUnknownSupplier > 0 && <span>{formatActivitiesWithUnknownSupplier(total.activitiesWithUnknownSupplier)}</span>}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mt: 2.5, mb: 1 }}>
        <Box>
          <Typography variant="h3" sx={{ fontSize: '0.9375rem', fontWeight: 500 }}>
            Leverandører på trinnet
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            Klik på en søjle for at filtrere listen på paragraf – klik på et grønt segment for også at filtrere på intern/ekstern
          </Typography>
        </Box>
        {filter !== undefined && (
          <Button size="small" onClick={() => setFilter(undefined)}>
            Vis alle ({filterLabel(filter)})
          </Button>
        )}
      </Box>
      <SupplierTable rows={rows} />
      <Typography sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
        Viser {formatNumber(rows.length)} af {formatNumber(suppliers.suppliers.length)} rækker på Trin {step.step} · {step.title}
      </Typography>
    </Box>
  );
}

function filterLabel(filter: SupplierFilter) {
  if (filter.type === undefined) {
    return filter.paragraph;
  }
  return `${filter.paragraph} · ${filter.type === InterventionSupplierType.Internal ? 'interne' : 'eksterne'}`;
}

function SupplierTable({ rows }: { rows: InterventionSupplierModel[] }) {
  return (
    <Table size="small" sx={{ '& th': { fontSize: '0.75rem', color: 'text.secondary' }, '& td': { fontSize: '0.8125rem' } }}>
      <TableHead>
        <TableRow>
          <TableCell>Leverandør</TableCell>
          <TableCell>CVR/P-nr.</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Paragraf</TableCell>
          <TableCell align="right">Indsatser</TableCell>
          <TableCell align="right">Borgere</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={6} sx={{ color: 'text.secondary' }}>
              Ingen leverandører
            </TableCell>
          </TableRow>
        )}
        {rows.map(row => (
          <TableRow key={`${row.paragraph}-${row.type}-${row.cvr ?? row.productionUnitIdentifier ?? ''}-${row.name}`}>
            <TableCell sx={{ fontWeight: 600, color: '#16323d' }}>{row.name}</TableCell>
            <TableCell sx={{ color: 'text.secondary' }}>{identifierOf(row)}</TableCell>
            <TableCell>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <Box component="span" sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: supplierTypes[row.type].color }} />
                {supplierTypes[row.type].label}
              </Box>
            </TableCell>
            <TableCell>{row.paragraph}</TableCell>
            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatNumber(row.activities)}
            </TableCell>
            <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatNumber(row.citizens)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function identifierOf(row: InterventionSupplierModel) {
  if (row.cvr) {
    return `CVR ${row.cvr}`;
  }
  return row.productionUnitIdentifier ? `P-nr. ${row.productionUnitIdentifier}` : '–';
}

function SupplierLegend() {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1, fontSize: '0.75rem', color: '#5b6b73' }}>
      <LegendSwatch color={internalCostColor} label="Interne leverandører" />
      <LegendSwatch color={externalCostColor} label="Eksterne leverandører" />
      <span>Søjle = antal leverandører på paragraffen · stablet på interne + eksterne</span>
    </Box>
  );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box component="span" sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color }} />
      {label}
    </Box>
  );
}
